import React, { useMemo, useState } from 'react';
import { Box, Button, Divider, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ModalCard from '../ui/ModalCard';
import TextInput from '../ui/TextInput';
import StatusIconCircle from '../ui/StatusIconCircle';
import { CheckIcon, EyeIcon, EyeOffIcon, PencilIcon, XIcon } from '../icons';
import { RegisterValidationError, RegisterUserViolation, registerUser } from '../../api/authApi';

const PASSWORD_RULES = [
  { key: 'upper', test: (v: string) => /[A-Z]/.test(v) },
  { key: 'lower', test: (v: string) => /[a-z]/.test(v) },
  { key: 'digit', test: (v: string) => /[0-9]/.test(v) },
  { key: 'special', test: (v: string) => /[^A-Za-z0-9]/.test(v) },
] as const;

const CHIP_PASS = { bg: '#d9f2e3', fg: '#30986c' };
const CHIP_FAIL = { bg: '#ffe1e1', fg: '#f73c3b' };
const SUCCESS_GREEN = '#30986c';

type Phase = 'details' | 'password';

interface FieldErrors {
  firstName?: string;
  lastName?: string;
  password?: string;
}

const FIELD_OF_VIOLATION: Partial<Record<RegisterUserViolation, keyof FieldErrors>> = {
  FIRST_NAME_EMPTY: 'firstName',
  FIRST_NAME_TOO_SHORT: 'firstName',
  FIRST_NAME_TOO_LONG: 'firstName',
  LAST_NAME_EMPTY: 'lastName',
  LAST_NAME_TOO_SHORT: 'lastName',
  LAST_NAME_TOO_LONG: 'lastName',
  PASSWORD_EMPTY: 'password',
  PASSWORD_TOO_SHORT: 'password',
  PASSWORD_TOO_LONG: 'password',
  PASSWORD_CONTAINS_WHITESPACE: 'password',
};

const VIOLATION_KEY: Record<RegisterUserViolation, string> = {
  EMAIL_NOT_UNIQUE: 'register.complete.errors.emailNotUnique',
  EMAIL_INVALID: 'register.complete.errors.generic',
  EMAIL_EMPTY: 'register.complete.errors.generic',
  PASSWORD_EMPTY: 'register.complete.errors.passwordEmpty',
  PASSWORD_TOO_SHORT: 'register.complete.errors.passwordTooShort',
  PASSWORD_TOO_LONG: 'register.complete.errors.passwordTooLong',
  PASSWORD_CONTAINS_WHITESPACE: 'register.complete.errors.passwordWhitespace',
  FIRST_NAME_EMPTY: 'register.complete.errors.firstNameEmpty',
  FIRST_NAME_TOO_SHORT: 'register.complete.errors.firstNameTooShort',
  FIRST_NAME_TOO_LONG: 'register.complete.errors.firstNameTooLong',
  LAST_NAME_EMPTY: 'register.complete.errors.lastNameEmpty',
  LAST_NAME_TOO_SHORT: 'register.complete.errors.lastNameTooShort',
  LAST_NAME_TOO_LONG: 'register.complete.errors.lastNameTooLong',
  ROLE_EMPTY: 'register.complete.errors.generic',
};

interface RegisterCompleteFormProps {
  email: string;
  onSuccess: () => void;
  jwtToken: string;
}

export default function RegisterCompleteForm({ email, onSuccess, jwtToken }: RegisterCompleteFormProps) {
  const { t } = useTranslation();

  const [phase, setPhase] = useState<Phase>('details');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  // const [phone, setPhone] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [generalErrorKey, setGeneralErrorKey] = useState<string | null>(null);

  const detailsValid = firstName.trim() !== '' && lastName.trim() !== '';

  const ruleResults = useMemo(
    () => PASSWORD_RULES.map((rule) => ({ key: rule.key, passed: rule.test(password) })),
    [password],
  );
  const passwordValid = ruleResults.every((r) => r.passed);
  const passwordsMatch = confirmPassword !== '' && password === confirmPassword;
  const canSubmit = detailsValid && passwordValid && passwordsMatch && !submitting;

  const handleNext = () => {
    if (detailsValid) setPhase('password');
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setFieldErrors({});
    setGeneralErrorKey(null);
    try {
      await registerUser({ email, password, firstName, lastName }, jwtToken);
      // TODO(backend): phone isn't accepted by /users/register yet, so it's
      // collected but not sent until the backend supports it.
      onSuccess();
    } catch (err) {
      if (err instanceof RegisterValidationError) {
        const nextFieldErrors: FieldErrors = {};
        let generalKey: string | null = null;
        let hasNameViolation = false;
        err.violations.forEach((violation) => {
          const field = FIELD_OF_VIOLATION[violation];
          if (field) {
            nextFieldErrors[field] = VIOLATION_KEY[violation];
            if (field === 'firstName' || field === 'lastName') hasNameViolation = true;
          } else {
            generalKey = VIOLATION_KEY[violation];
          }
        });
        setFieldErrors(nextFieldErrors);
        setGeneralErrorKey(generalKey);
        if (hasNameViolation) setPhase('details');
      } else {
        setGeneralErrorKey('register.complete.errors.generic');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalCard title={t('register.cardTitle')} onSubmit={handleSubmit}>
      <StatusIconCircle icon={<CheckIcon size={28} />} variant="accent" />

      <Box>
        <Typography variant="h3" component="h1">
          {t('register.complete.headline')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          {t('register.complete.subheadline')}
        </Typography>
      </Box>

      {generalErrorKey && (
        <Typography variant="body2" sx={(theme) => ({ color: theme.app.destructive })}>
          {t(generalErrorKey)}
        </Typography>
      )}

      <Divider />

      {phase === 'details' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <StepHeader number={1} active title={t('register.complete.step1Title')} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('register.complete.step1Description')}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <TextInput
              id="register-first-name"
              autoComplete="given-name"
              label={t('register.complete.firstNameLabel')}
              placeholder={t('register.complete.firstNamePlaceholder')}
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                setFieldErrors((prev) => ({ ...prev, firstName: undefined }));
              }}
            />
            {fieldErrors.firstName && (
              <Typography variant="caption" sx={(theme) => ({ color: theme.app.destructive })}>
                {t(fieldErrors.firstName)}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <TextInput
              id="register-last-name"
              autoComplete="family-name"
              label={t('register.complete.lastNameLabel')}
              placeholder={t('register.complete.lastNamePlaceholder')}
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                setFieldErrors((prev) => ({ ...prev, lastName: undefined }));
              }}
            />
            {fieldErrors.lastName && (
              <Typography variant="caption" sx={(theme) => ({ color: theme.app.destructive })}>
                {t(fieldErrors.lastName)}
              </Typography>
            )}
          </Box>
          {/*<TextInput*/}
          {/*  id="register-phone"*/}
          {/*  type="tel"*/}
          {/*  autoComplete="tel"*/}
          {/*  label={t('register.complete.phoneLabel')}*/}
          {/*  placeholder={t('register.complete.phonePlaceholder')}*/}
          {/*  value={phone}*/}
          {/*  onChange={(e) => setPhone(e.target.value)}*/}
          {/*/>*/}

          <Button
            type="button"
            variant="contained"
            fullWidth
            disabled={!detailsValid}
            onClick={handleNext}
            sx={(theme) => ({
              mt: 1,
              backgroundColor: theme.app.primary,
              color: theme.app.primaryForeground,
              '&:hover': { backgroundColor: theme.app.primaryHover },
            })}
          >
            {t('register.complete.next')}
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              width: 40,
              height: 40,
              borderRadius: '9999px',
              backgroundColor: SUCCESS_GREEN,
              color: '#ffffff',
            }}
          >
            <CheckIcon size={20} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {`${firstName} ${lastName}`.trim()}
            </Typography>
            {/*{phone.trim() && (*/}
            {/*  <Typography variant="body2" sx={{ color: 'text.secondary' }}>*/}
            {/*    {phone}*/}
            {/*  </Typography>*/}
            {/*)}*/}
          </Box>
          <Button
            type="button"
            variant="text"
            startIcon={<PencilIcon size={16} />}
            onClick={() => setPhase('details')}
            sx={(theme) => ({
              flexShrink: 0,
              color: theme.app.secondaryForeground,
              backgroundColor: theme.app.secondary,
              '&:hover': { backgroundColor: theme.app.border },
            })}
          >
            {t('register.complete.edit')}
          </Button>
        </Box>
      )}

      <Divider />

      {phase === 'password' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <StepHeader number={2} active title={t('register.complete.step2Title')} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('register.complete.step2Description')}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextInput
              id="register-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              label={t('register.complete.passwordLabel')}
              placeholder={t('register.complete.passwordPlaceholder')}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setFieldErrors((prev) => ({ ...prev, password: undefined }));
              }}
              endAdornment={
                <IconButton
                  size="small"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={t(showPassword ? 'register.complete.hidePassword' : 'register.complete.showPassword')}
                  sx={{ color: 'text.secondary' }}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </IconButton>
              }
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {ruleResults.map((rule) => (
                <RuleChip key={rule.key} label={t(`register.complete.rules.${rule.key}`)} passed={rule.passed} />
              ))}
            </Box>
            {fieldErrors.password && (
              <Typography variant="caption" sx={(theme) => ({ color: theme.app.destructive })}>
                {t(fieldErrors.password)}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <TextInput
              id="register-confirm-password"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              label={t('register.complete.confirmPasswordLabel')}
              placeholder={t('register.complete.confirmPasswordPlaceholder')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              endAdornment={
                <IconButton
                  size="small"
                  onClick={() => setShowConfirm((prev) => !prev)}
                  aria-label={t(showConfirm ? 'register.complete.hidePassword' : 'register.complete.showPassword')}
                  sx={{ color: 'text.secondary' }}
                >
                  {showConfirm ? <EyeOffIcon /> : <EyeIcon />}
                </IconButton>
              }
            />
            {confirmPassword !== '' && !passwordsMatch && (
              <Typography variant="caption" sx={(theme) => ({ color: theme.app.destructive })}>
                {t('register.complete.passwordMismatch')}
              </Typography>
            )}
          </Box>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={!canSubmit}
            sx={(theme) => ({
              mt: 1,
              backgroundColor: theme.app.primary,
              color: theme.app.primaryForeground,
              '&:hover': { backgroundColor: theme.app.primaryHover },
            })}
          >
            {t('register.complete.submit')}
          </Button>
        </Box>
      ) : (
        <StepHeader number={2} active={false} title={t('register.complete.step2Title')} />
      )}
    </ModalCard>
  );
}

function StepHeader({ number, active, title }: { number: number; active: boolean; title: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          width: 30,
          height: 30,
          borderRadius: '9999px',
          fontSize: 14,
          fontWeight: 600,
          backgroundColor: active ? theme.app.primary : theme.app.secondary,
          color: active ? theme.app.primaryForeground : theme.app.mutedForeground,
        })}
      >
        {number}
      </Box>
      <Typography
        variant="h4"
        component="h2"
        sx={(theme) => ({ color: active ? theme.app.foreground : theme.app.mutedForeground })}
      >
        {title}
      </Typography>
    </Box>
  );
}

function RuleChip({ label, passed }: { label: string; passed: boolean }) {
  const colors = passed ? CHIP_PASS : CHIP_FAIL;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.5,
        borderRadius: '9999px',
        backgroundColor: colors.bg,
        color: colors.fg,
      }}
    >
      {passed ? <CheckIcon size={14} /> : <XIcon size={14} />}
      <Typography variant="caption" sx={{ fontWeight: 600, color: 'inherit' }}>
        {label}
      </Typography>
    </Box>
  );
}
